import {findKeyByValue} from 'common/util/typescript';
import {EventFrequencyClause} from 'common/widgets/cohorts-query-builder/models/builder/event-frequency-clause';
import {PeoplePropertyClause} from 'common/widgets/cohorts-query-builder/models/builder/people-property-clause';
import {CohortClause} from 'common/widgets/cohorts-query-builder/models/builder/cohort-clause';
import {PropertyFilter} from 'common/widgets/cohorts-query-builder/models/builder/property-filter';
import {PropertyFilter as CommonPropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  LogicalOperator,
  PredefinedCohort,
  Property,
} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {FilterValue} from 'common/widgets/cohorts-query-builder/models/types/expression-types';
import {DatetimeOperator, FrequencyOperator} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {ClauseOperators, FilterOperator} from 'common/widgets/cohorts-query-builder/models/types/builder-state-types';
import {ANY_EVENT} from 'common/widgets/query-block/events-menu/constants';
import {
  EventFilters,
  FiltersOperator,
  QueryBehaviorEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {getInlineCustomPropertyId} from 'common/widgets/custom-property-modal/util';
import {
  BasePropertyTypes,
  BooleanOperator,
  FilterOperator as CommonFilterOperator,
  FilterValue as CommonFilterValue,
  ListOperator,
  NumberOperator,
  PropertyType,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {ResourceType} from 'common/report/constants';
import {renameEvent} from 'common/report/util/events';
import {SerializedDatetime} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {CustomProperty, CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {BaseTimeUnit, DateRangeType} from 'common/widgets/date-range-picker/models/types';
import {customEventsStore} from 'common/report/stores/custom-events';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {ClausePosition, StagedCohort} from 'common/widgets/cohorts-query-builder/models/builder/staged-cohort';

import {
  convertDateRangeToStagedDatetime,
  convertSerializedDatetimeToDateRange,
  DatetimeOperatorToDateRangeTypeMapping,
  getDateRangeFromBehavior,
  getDateRangeLabelFromBehavior,
} from './time';

type FilterNegationModifier = (propertyFilter: CommonPropertyFilter) => CommonPropertyFilter;

export const reversibleFilterOperator: (
  a: CommonFilterOperator,
  b: CommonFilterOperator,
) => {[key: string]: FilterNegationModifier} = (a, b) => ({
  [a]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: b,
    }),
  [b]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: a,
    }),
});

export const FilterOperatorNegationMapping: {[key: string]: FilterNegationModifier} = {
  ...reversibleFilterOperator(StringOperator.Contains, StringOperator.DoesNotContain),
  ...reversibleFilterOperator(StringOperator.Equals, StringOperator.DoesNotEqual),
  ...reversibleFilterOperator(StringOperator.Set, StringOperator.NotSet),

  ...reversibleFilterOperator(NumberOperator.EqualTo, NumberOperator.NotEqualTo),
  ...reversibleFilterOperator(NumberOperator.Between, NumberOperator.NotBetween),
  ...reversibleFilterOperator(NumberOperator.AtLeast, NumberOperator.LessThan),
  // Replicate less than or equal to behavior (at most)
  [NumberOperator.GreaterThan]: (propertyFilter) =>
    new CommonPropertyFilter({
      ...propertyFilter.getAttributes(),
      filterOperator: NumberOperator.LessThan,
      filterValue: (Array.isArray(propertyFilter.filterValue)
        ? propertyFilter.filterValue
        : [propertyFilter.filterValue]
      ).map((v) => parseInt(v as string, 10) + 1),
    }),
  /** @todo: [shawn] Replace the operator with `at most` <= once it lands */

  ...reversibleFilterOperator(BooleanOperator.True, BooleanOperator.False),
  ...reversibleFilterOperator(ListOperator.Contains, ListOperator.DoesNotContain),

  ...reversibleFilterOperator(DateRangeType.Between, DateRangeType.NotBetween),
  ...reversibleFilterOperator(DateRangeType.On, DateRangeType.NotOn),
  /** @todo: [shawn] Replace with `not the last` and `not before the last` */
  ...reversibleFilterOperator(DateRangeType.RelativeAfter, DateRangeType.RelativeBefore),

  /** @todo: [shawn] We need the before operator to correctly negate this */
  [DateRangeType.Since]: () => {
    // Surface this to rollbar
    throw new Error(`Unsupported negation for Since operator`);
  },
};

export const PropertyTypeToResourceTypeMapping: {[key: string]: ResourceType} = {
  [Property.Event]: ResourceType.Event,
  [Property.Cohort]: ResourceType.Cohort,
  [Property.User]: ResourceType.User,
};

export function getResourceTypeFromPropertyType(propertyType: Property): ResourceType {
  if (!PropertyTypeToResourceTypeMapping[propertyType]) {
    throw new Error(`Invalid property type ${propertyType} for resourceType conversion`);
  }

  return PropertyTypeToResourceTypeMapping[propertyType];
}

export function getFiltersOperatorFromLiteralClauseOperators(clauseOperators: ClauseOperators): FiltersOperator {
  // return `and` as a default for single clause literals
  if (!clauseOperators) {
    return FiltersOperator.And;
  }

  const [baseOperator, extendedOperator] = clauseOperators;

  if (baseOperator === null) {
    return FiltersOperator.Then;
  }

  if (baseOperator === LogicalOperator.And) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.AndAny : FiltersOperator.And;
  }

  if (baseOperator === LogicalOperator.Or) {
    return extendedOperator === LogicalOperator.Or ? FiltersOperator.OrAny : FiltersOperator.Or;
  }

  throw new Error(`Invalid clauseOperators ${clauseOperators}`);
}

export function getGroupingOperatorFromLogicalOperators(
  operators: LogicalOperator[],
): Array<FiltersOperator.And | FiltersOperator.Or> {
  return operators.map((operator) => (operator === LogicalOperator.And ? FiltersOperator.And : FiltersOperator.Or));
}

export function getLiteralClauseOperatorsFromFiltersOperator(filtersOperator: FiltersOperator): ClauseOperators {
  // filtersOperator defaults to `And`
  if (!filtersOperator) {
    return [LogicalOperator.And, LogicalOperator.And];
  }

  switch (filtersOperator) {
    case FiltersOperator.Then:
      return [null, null];
    case FiltersOperator.And:
      return [LogicalOperator.And, LogicalOperator.And];
    case FiltersOperator.AndAny:
      return [LogicalOperator.And, LogicalOperator.Or];
    case FiltersOperator.Or:
      return [LogicalOperator.Or, LogicalOperator.And];
    case FiltersOperator.OrAny:
      return [LogicalOperator.Or, LogicalOperator.Or];

    default:
      throw new Error(`Invalid filtersOperator ${filtersOperator}`);
  }
}

export function getFiltersOperatorFromPropertyFilterOperator(propertyFilterOperator: LogicalOperator): FiltersOperator {
  if (propertyFilterOperator === LogicalOperator.And) {
    return FiltersOperator.And;
  }

  if (propertyFilterOperator === LogicalOperator.Or) {
    return FiltersOperator.Or;
  }

  throw new Error(`Invalid property filter operator ${propertyFilterOperator}`);
}

export const FrequencyOperatorToNumericFilterOperatorMapping: {[key in FrequencyOperator]: NumberOperator} = {
  [FrequencyOperator.AtLeast]: NumberOperator.AtLeast,
  [FrequencyOperator.Between]: NumberOperator.Between,
  [FrequencyOperator.EqualTo]: NumberOperator.EqualTo,
  [FrequencyOperator.GreaterThan]: NumberOperator.GreaterThan,
  [FrequencyOperator.LessThan]: NumberOperator.LessThan,
};

export function convertCommonPropertyFilterToCohortsPropertyFilter(filter: CommonPropertyFilter): PropertyFilter {
  let operator: FilterOperator = filter.filterOperator as any;
  let value: FilterValue = filter.filterValue;
  let negated = false;

  if (filter.propertyType === BasePropertyTypes.Datetime) {
    operator = findKeyByValue(DatetimeOperatorToDateRangeTypeMapping, filter.filterOperator);

    // Attempt to map to negated value
    if (!operator) {
      operator = findKeyByValue(
        DatetimeOperatorToDateRangeTypeMapping,
        FilterOperatorNegationMapping[filter.filterOperator](filter).filterOperator,
      );

      if (!operator) {
        throw new Error(`Invalid filter operator ${filter.filterOperator}`);
      }

      negated = true;
    }

    value = convertDateRangeToStagedDatetime(filter.filterValue as DateRange, operator);

    // Special-case for future dates
    if (
      operator === DatetimeOperator.Relative &&
      typeof value !== `string` &&
      !Array.isArray(value) &&
      value.window?.value < 0
    ) {
      operator = DatetimeOperator.RelativeFuture;
    }
  }

  const propertyFilter = new PropertyFilter({
    operator,
    type: filter.propertyType as BasePropertyTypes,
    property: filter.propertyName,
    value,
    propertyType: findKeyByValue(PropertyTypeToResourceTypeMapping, filter.resourceType) as Property,
    ...(filter.customPropertyId
      ? {
          customPropertyId: filter.customPropertyId,
        }
      : {}),
    dataGroupId: filter.dataGroupId,
    joinPropertyType: filter.joinPropertyType,
    negated,
  });

  if (
    new PropertyFilter()
      .setStagedFilterClause({...propertyFilter, joinPropertyType: propertyFilter.joinPropertyType as any})
      .hasInvalidStagedFilterClause()
  ) {
    throw new Error(`Invalid propertyFilter, staged clause includes invalid values`);
  }

  return propertyFilter;
}

export function convertPropertyFilterToCommon(filter: PropertyFilter): CommonPropertyFilter {
  let filterValue: CommonFilterValue = filter.value;
  let filterOperator: CommonFilterOperator;

  // Transform filter types
  switch (filter.type) {
    case BasePropertyTypes.Boolean:
      filterOperator = filter.operator as BooleanOperator;
      break;
    case BasePropertyTypes.Datetime:
      filterOperator = DatetimeOperatorToDateRangeTypeMapping[filter.operator as DatetimeOperator];
      filterValue = convertSerializedDatetimeToDateRange(
        filter.value as SerializedDatetime,
        filter.operator as DatetimeOperator,
      );
      break;
    case BasePropertyTypes.List:
      filterOperator = filter.operator as ListOperator;
      break;
    case BasePropertyTypes.String:
      filterOperator = filter.operator as StringOperator;
      break;
    case BasePropertyTypes.Number:
      filterOperator = filter.operator as NumberOperator;
      break;
    case BasePropertyTypes.Dimension:
      filterOperator = filter.operator as any;
      break;
    default:
      throw new Error(`Unhandled property filter type in filter ${JSON.stringify({filter})}`);
  }

  const propertyFilter = new CommonPropertyFilter({
    filterOperator,
    filterValue,
    propertyName: filter.property,
    ...(filter.customPropertyId
      ? {
          customPropertyId: filter.customPropertyId,
        }
      : {}),
    propertyType: filter.type,
    resourceType: getResourceTypeFromPropertyType(filter.propertyType),
    propertyDefaultType: filter.type,
    dataGroupId: filter.dataGroupId,
    joinPropertyType: filter.joinPropertyType as PropertyType,
  });

  // Operator negations are only applied to filters under People property clauses
  // Event frequency clauses are negated with a frequency of 0 and cohort clauses have negated properties built-in
  return filter.isNegated() ? FilterOperatorNegationMapping[filterOperator](propertyFilter) : propertyFilter;
}

export function getEventFromCohortEvent(event): EventItem {
  if (!event) {
    return {
      label: ANY_EVENT.label,
      value: ANY_EVENT.value,
    };
  }

  if (event.startsWith(`$custom_event:`)) {
    const id = Number(event.replace(/^\$custom_event:/, ``));

    return {
      id,
      custom: true,
      label: customEventsStore?.value[id]?.name ?? event,
      value: event,
    };
  }

  return {label: renameEvent(event), value: event};
}

export function getCohortEventFromEventItem(event: EventItem): string {
  if (event.value === ANY_EVENT.value) {
    return ``;
  }

  if (event.custom) {
    return `$custom_event:${event.id}`;
  }

  return event.value;
}

export function applyCommonPropertyFilterToClause(
  filter: CommonPropertyFilter,
  clausePosition: ClausePosition,
  propertyFilterIdx: number,
  stagedCohort: StagedCohort,
) {
  const propertyFilter = convertCommonPropertyFilterToCohortsPropertyFilter(filter);
  return stagedCohort
    .addPropertyFilterToClause(clausePosition)
    .updateStagedFilterClause(
      {
        ...propertyFilter,
        joinPropertyType: propertyFilter.joinPropertyType as any,
      },
      clausePosition,
      propertyFilterIdx,
    )
    .commitStagedFilterClause(clausePosition, propertyFilterIdx);
}

export function applyCommonPropertyFiltersToStagedCohort(
  filters: EventFilters,
  clausePosition: ClausePosition,
  filtersOperator: FiltersOperator,
  stagedCohort: StagedCohort,
) {
  let updatedStagedCohort = filters.reduce(
    (stagedCohort, filter, i) => applyCommonPropertyFilterToClause(filter, clausePosition, i, stagedCohort),
    stagedCohort,
  );

  if (
    (filtersOperator === FiltersOperator.Or &&
      updatedStagedCohort.getPropertyFilterOperator(clausePosition) !== LogicalOperator.Or) ||
    (filtersOperator === FiltersOperator.And &&
      updatedStagedCohort.getPropertyFilterOperator(clausePosition) !== LogicalOperator.And)
  ) {
    updatedStagedCohort = updatedStagedCohort.togglePropertyFilterOperator(clausePosition);
  }

  return updatedStagedCohort;
}

export function applyBehaviorFilterToStagedCohort(
  filter: CommonPropertyFilter,
  clausePosition: ClausePosition,
  stagedCohort: StagedCohort,
): StagedCohort {
  const {customProperty, filterValue, filterOperator} = filter;
  const behavior: QueryBehaviorEntry = customProperty.behavior;
  let frequency: [number, number] | [number] = [0, 0];

  if (Array.isArray(filterValue) && filterValue.length === 2) {
    frequency = filterValue as [number, number];
  } else if (filterValue <= 1) {
    frequency = [filterValue] as [number];
  } else {
    frequency = [filterValue, filterValue] as [number, number];
  }

  let updatedStagedCohort = stagedCohort
    .updateEvent(getCohortEventFromEventItem(behavior.event), clausePosition)
    .updateFrequencyValue(frequency, clausePosition)
    .updateFrequencyOperator(
      findKeyByValue(FrequencyOperatorToNumericFilterOperatorMapping, filterOperator),
      clausePosition,
    );

  /** @todo: [shawn] Organize in time transformations */
  // apply daterange to behavior
  if (behavior.dateRange.type === DateRangeType.RelativeAfter) {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorCustomRange(
      behavior.dateRange.window.unit as BaseTimeUnit,
      behavior.dateRange.window.value,
      clausePosition,
    );
  } else if (behavior.dateRange.type === DateRangeType.Since) {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorDateRange(
      behavior.dateRange.from as string,
      null,
      clausePosition,
    );
  } else {
    updatedStagedCohort = updatedStagedCohort.updateBehaviorDateRange(
      behavior.dateRange.from as string,
      behavior.dateRange.to as string,
      clausePosition,
    );
  }

  updatedStagedCohort = applyCommonPropertyFiltersToStagedCohort(
    behavior.filters,
    clausePosition,
    behavior.filtersOperator,
    updatedStagedCohort,
  );

  return updatedStagedCohort;
}

export function getEventFiltersFromEventsFrequencyClause(clause: EventFrequencyClause): EventFilters {
  const {eventSelector} = clause.behavior.count;

  const additionalFilters = EventFrequencyClause.getPropertyFiltersAndOperator(
    eventSelector.selector,
  ).propertyFilters.map(convertPropertyFilterToCommon);

  const event = getEventFromCohortEvent(eventSelector.event);

  const label = `${event.label} ${getDateRangeLabelFromBehavior(clause.behavior, clause.isNegated())}`;

  const behavior: QueryBehaviorEntry = {
    type: SelectorTypeDefinitions.Behavior,
    dateRange: getDateRangeFromBehavior(clause.behavior),
    event,
    filters: additionalFilters,
    filtersOperator: getFiltersOperatorFromPropertyFilterOperator(clause.propertyFilterOperator),
  };

  const customProperty: CustomProperty = {
    name: label,
    behavior,
    description: ``,
    referencedBy: [],
    referencedDirectlyBy: [],
    referencedRawEventProperties: [],
    propertyType: PropertyType.Number,
    resourceType: CustomPropertyResourceType.People,
  };

  const customPropertyId = getInlineCustomPropertyId(null);

  const behaviorFilter = new CommonPropertyFilter({
    customProperty,
    customPropertyId,
    ...(clause.isNegated()
      ? {
          filterOperator: NumberOperator.EqualTo,
          filterValue: 0,
        }
      : {
          /** @todo: [shawn] Missing `count total on unique days` implementation which is equivalent to distinct_count Date(days) */
          filterOperator: FrequencyOperatorToNumericFilterOperatorMapping[clause.frequencyOperator],
          filterValue: clause.frequency.length === 1 ? clause.frequency[0] : clause.frequency,
        }),
    propertyType: PropertyType.Number,
    propertyName: label,
    resourceType: ResourceType.User,
  });

  return [behaviorFilter];
}

export function getEventFiltersFromPeoplePropertyClause(clause: PeoplePropertyClause): EventFilters {
  return clause.propertyFilters.map(convertPropertyFilterToCommon);
}

/** @todo: Discuss this format and support for it in the UI, the common QB currently doesn't render cohort filters */
export function getEventFiltersFromCohortClause(clause: CohortClause): EventFilters {
  if (clause.cohortId !== PredefinedCohort.AllUsers) {
    return [
      createCohortCommonPropertyFilter(clause.cohortId, clause.negated),
      ...clause.propertyFilters.map(convertPropertyFilterToCommon),
    ];
  }

  return clause.propertyFilters.map(convertPropertyFilterToCommon);
}

export function createCohortCommonPropertyFilter(cohortId: number, negated = false) {
  return new CommonPropertyFilter({
    filterValue: [
      {
        cohort: {
          id: cohortId,
          negated,
        },
      },
    ],
    propertyType: PropertyType.List,
    filterOperator: negated ? ListOperator.DoesNotContain : ListOperator.Contains,
    resourceType: ResourceType.Cohort,
  });
}
